import React, { useMemo } from 'react';
import { Button } from '@fluentui/react-components';
import classnames from 'classnames';

import './CustomButton.css';

/**
 * CustomButton - Standardized button component (Fluent UI)
 *
 * Uses Fluent's Button and theme tokens. Colors come from the theme (FluentProvider);
 * we only add CSS overrides where Fluent has no equivalent (outline+primary, success/danger/warning).
 *
 * Fluent default appearances (theme tokens):
 * - primary:   background = colorBrandBackground, text = colorNeutralForegroundOnBrand
 * - secondary: background = colorNeutralBackground1, text = colorNeutralForeground1, border = colorNeutralStroke1
 * - outline:   transparent bg, neutral border/text (no "outline primary" in Fluent → we override)
 * - subtle:    colorSubtleBackground, colorNeutralForeground2 (ghost style)
 * - transparent: transparent bg, colorNeutralForeground2
 *
 * @example
 * <CustomButton variant="outline" icon={<Plus size={16} />}>Create</CustomButton>
 * <CustomButton variant="ghost" icon={<Edit size={16} />} />
 * <CustomButton variant="solid">Save</CustomButton>
 */
/**
 * Also extends native button attributes (via `extends`) so Fluent Tooltip can pass through trigger props
 * (ref, onPointerEnter/Leave, onFocus/onBlur, aria-*, etc.) — see TooltipTriggerProps in @fluentui/react-tooltip.
 * Common DOM props are listed explicitly below for readability; additional HTML attributes still apply via `extends`.
 */
export interface CustomButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'hidden'> {
    /**
     * Button variant
     * - 'solid': Filled button (default)
     * - 'outline': Button with 1px border
     * - 'ghost': Tertiary. No background or border at rest, neutral gray hover/pressed fill (for icon-only)
     * - 'transparent': No border, hover effect is only for the text. Automatically applies navigation menu button styling:
     *   - Always: 'btn-block text-left font-size-md text-dark'
     *   - When active: 'font-weight-bold'
     *   - When not active: 'bg-white'
     */
    variant?: 'solid' | 'outline' | 'ghost' | 'transparent';
    appearance?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning';

    /**
     * Button size
     * - 'small': Small size
     * - 'medium': Medium size (default)
     * - 'large': Large size
     */
    size?: 'small' | 'medium' | 'large';

    /**
     * Button outline shape, straight through to Fluent.
     * - 'rounded': the default radius
     * - 'circular': a pill / round icon button (a floating action button)
     * - 'square': no radius
     */
    shape?: 'rounded' | 'circular' | 'square';

    /**
     * Icon to display (ReactNode - can be FontAwesome, Lucide, or any icon component)
     * - If icon is provided and children exist: icon + text button
     * - If icon is provided and no children: icon-only button (auto-detected)
     */
    icon?: React.ReactNode;
    iconPosition?: 'before' | 'after';
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
    type?: 'button' | 'submit' | 'reset';
    form?: string; // HTML form attribute
    children?: React.ReactNode;
    id?: string;
    /** When true, renders nothing (toolbar slot placeholder). Not the HTML `hidden` attribute. */
    hidden?: boolean;
    active?: boolean;
    className?: string; // Only for utility classes (margin, padding)
    style?: React.CSSProperties; // Inline styles for layout
    title?: string;
}

type FluentAppearance = 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent';

/**
 * Forwards ref and native event/ARIA props to Fluent Button so Tooltip and similar triggers work
 * (see dashboards/components/tooltip).
 */
const CustomButton = React.forwardRef<HTMLButtonElement, CustomButtonProps>(function CustomButton(
    {
        variant = 'solid',
        appearance = 'primary',
        size = 'medium',
        shape,
        icon,
        iconPosition,
        className,
        style,
        children,
        onClick,
        disabled,
        type = 'button',
        form,
        id,
        hidden,
        active,
        title,
        ...rest
    },
    ref,
) {
    const { fluentAppearance, semanticClass } = useMemo(() => {
        // Map to Fluent's single appearance; Fluent handles primary/secondary/outline/subtle/transparent via theme
        let fluent: FluentAppearance;
        if (variant === 'outline') {
            fluent = 'outline';
        } else if (variant === 'ghost') {
            fluent = 'subtle';
        } else if (variant === 'transparent') {
            fluent = 'transparent';
        } else {
            fluent = appearance === 'secondary' ? 'secondary' : 'primary';
        }
        // Only add override class when Fluent has no equivalent: outline+primary or semantic (success/danger/warning).
        // ghost+primary is deliberately NOT overridden: the tertiary button is neutral by design
        // (transparent at rest, gray hover/pressed), which is exactly Fluent's `subtle` appearance.
        const needsPrimaryOverride = appearance === 'primary' && variant === 'outline';
        const isSemantic = ['success', 'danger', 'warning'].includes(appearance);
        const semantic = needsPrimaryOverride
            ? 'custom-btn-primary'
            : isSemantic
              ? `custom-btn-${appearance}`
              : undefined;
        return { fluentAppearance: fluent, semanticClass: semantic };
    }, [variant, appearance]);

    const classes = classnames(
        semanticClass,
        variant === 'solid' && active && 'custom-btn-solid-active',
        variant === 'outline' && 'custom-btn-outline',
        variant === 'outline' && active && 'custom-btn-outline-active',
        variant === 'ghost' && 'custom-btn-ghost',
        variant === 'ghost' && active && 'custom-btn-ghost-active',
        variant === 'transparent' && 'custom-btn-transparent',
        variant === 'transparent' && active && 'custom-btn-transparent-active',
        className,
    );

    if (hidden) {
        return null;
    }

    return (
        <Button
            {...rest}
            ref={ref}
            appearance={fluentAppearance}
            size={size}
            shape={shape}
            icon={icon ? <span className="custom-btn-icon-wrapper">{icon}</span> : undefined}
            iconPosition={iconPosition}
            className={classes}
            style={style}
            onClick={onClick}
            disabled={disabled}
            type={type}
            form={form}
            id={id}
            hidden={hidden}
            title={title}
        >
            {children}
        </Button>
    );
});

export default CustomButton;
